import { uniqBy } from "lodash";
import { Location, BoundingBox } from "geolocation-utils";
import {
  doc,
  query,
  where,
  setDoc,
  getDoc,
  addDoc,
  getDocs,
  deleteDoc,
  Timestamp,
  collection,
  DocumentData,
} from "firebase/firestore";

import { Utils } from "utils";
import { castDocs } from "utils/helpers";
import { db, functions } from "configs/firebase";
import { httpsCallable } from "firebase/functions";
import { BOUNDING_BOXES } from "constants/locations";
import { COLLECTIONS, FUNCTIONS } from "constants/firebase";
import { Tag, Iterable, IBestBikingRoad, IPlace, ICategoryTag } from "types";

const document = (collection: string, id: string) => doc(db, collection, id);

export const tags = {
  list: () => {
    return getDocs(collection(db, COLLECTIONS.TAGS));
  },
  create: (data: Tag) => {
    return addDoc(collection(db, COLLECTIONS.TAGS), data);
  },
  edit: (id: string, data: any) => {
    return setDoc(document(COLLECTIONS.TAGS, id), data);
  },
  delete: (id: string) => {
    return deleteDoc(document(COLLECTIONS.TAGS, id));
  },
};

export const collectedPlaces = {
  list: (countries: string[], tags: string[]) => {
    const codes = countries.map(
      (item) => BOUNDING_BOXES.find((i) => i.name === item)!.code
    );

    const tagsChunks = Utils.Helpers.chunkArray(tags, 10);
    let queries: ReturnType<typeof query<DocumentData>>[] = [];

    console.log({ codes });

    for (const code of codes) {
      const queriesRefs = tagsChunks.map((item) =>
        // collection(db, COLLECTIONS.PLACES),
        query(
          collection(db, "Dump"),
          where("address_info.country_code", "==", code),
          where("category", "in", item)
          // where("tags", "array-contains-any", item)
        )
      );
      queries = [...queries, ...queriesRefs];
    }

    return Promise.all(queries.map(async (q) => await getDocs(q)));
  },
  forCountryCode: (code: string) => {
    const q = query(
      collection(db, COLLECTIONS.PLACES),
      where("address_info.country_code", "==", code)
    );
    return getDocs(q);
  },
  get: (id: string) => {
    const documentRef = document(COLLECTIONS.PLACES, id);
    return getDoc(documentRef);
  },
  delete: (id: string) => {
    return deleteDoc(document(COLLECTIONS.PLACES, id));
  },
  edit: (id: string, data: any) => {
    const ref = document(COLLECTIONS.PLACES, id);
    return setDoc(ref, data, { merge: true });
  },
};

export const places = {
  list: (countries: string[], tags: string[]) => {
    return collectedPlaces.list(countries, tags);
  },
};

export const boundaries = Object.freeze({
  list: () => {
    return getDocs(collection(db, "Boundaries"));
  },
  create: (countryCode: string, data: any) => {
    return setDoc(document("Boundaries", countryCode), data);
  },
});

export const countries = Object.freeze({
  list: () => {
    return getDocs(collection(db, COLLECTIONS.COUNTRIES));
  },
  get: (isoCode: string) => {
    const documentRef = document(COLLECTIONS.COUNTRIES, isoCode);
    return getDoc(documentRef);
  },
});

export const routes = Object.freeze({
  places: async (countryCodes: string[], tags: string[]) => {
    try {
      const tagsChunks = Utils.Helpers.chunkArray(tags, 10);

      let documents: Iterable<IPlace>[] = [];

      for (const code of countryCodes) {
        let countryPlaces: typeof documents = [];

        for (const tags of tagsChunks) {
          const constraints = [
            where("address_info.country_code", "==", code),
            where("tags", "array-contains-any", tags),
          ];

          const q = query(collection(db, COLLECTIONS.PLACES), ...constraints);
          const response = await getDocs(q);

          const places = Utils.Helpers.castDocs<Iterable<IPlace>>(response);
          countryPlaces = [...countryPlaces, ...places];
        }

        documents = uniqBy([...documents, ...countryPlaces], "id");
      }

      return Promise.resolve(documents);
    } catch (error) {
      return Promise.reject(error);
    }
  },
});

export type PlacesRequestOptions = {
  sortByRating?: boolean;
  sortByReviews?: boolean;
  boundingBox?: BoundingBox;
  circle?: { center: Location; radius: number };
};

export const categoryTags = {
  list: async ({
    onlyPublicCategories = false,
  }: {
    onlyPublicCategories?: boolean;
  }) => {
    try {
      const callable = httpsCallable<
        any,
        { categories: Iterable<ICategoryTag>[] }
      >(functions, FUNCTIONS.GET_CATEGORIES);
      const response = await callable();

      const categories = response.data.categories.filter((category) => {
        if (onlyPublicCategories) {
          return category.isPublic === true;
        }

        return true;
      });

      return Promise.resolve(categories);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  edit: (id: string, data: any) => {
    return setDoc(document(COLLECTIONS.CATEGORY_TAGS, id), data);
  },

  create: (data: any) => {
    return addDoc(collection(db, COLLECTIONS.CATEGORY_TAGS), data);
  },

  remove: (id: string) => {
    return deleteDoc(document(COLLECTIONS.CATEGORY_TAGS, id));
  },
};

export const bestBikingRoads = {
  list: async (countryCodes: string[]) => {
    const q = query(
      collection(db, COLLECTIONS.BEST_BIKING_ROADS),
      where("countryCode", "in", countryCodes)
    );
    const response = await getDocs(q);
    const documents = castDocs<Iterable<IBestBikingRoad>>(response);
    return documents;
  },

  edit: (id: string, data: any) => {
    return setDoc(document(COLLECTIONS.BEST_BIKING_ROADS, id), data, {
      merge: true,
    });
  },

  create: (data: any) => {
    return addDoc(collection(db, COLLECTIONS.BEST_BIKING_ROADS), data);
  },
};

export const graphhopperApiKeys = Object.freeze({
  create: (key: string, email: string, group: string) => {
    return addDoc(collection(db, COLLECTIONS.GRAPHHOPPER_API_KEYS), {
      key,
      email,
      group,
      createdAt: Timestamp.now(),
    });
  },

  delete: (id: string) => {
    return deleteDoc(doc(db, COLLECTIONS.GRAPHHOPPER_API_KEYS, id));
  },
});
